"use client";

import { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  EllipsisVertical,
  PencilLine,
  Trash,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

/**
 * Reusable DataTable component with pagination, search, and actions
 * @param {Object} props - Component props
 * @param {Array} props.data - Table data
 * @param {Array} props.columns - Column configuration
 * @param {number} props.totalPages - Total number of pages
 * @param {number} props.currentPage - Current page number
 * @param {Function} props.onPageChange - Page change handler
 * @param {Function} props.onSearch - Search handler
 * @param {boolean} props.isLoading - Loading state
 * @param {string} props.searchPlaceholder - Search input placeholder
 */
const DataTable = ({
  data = [],
  columns = [],
  totalPages = 1,
  currentPage = 1,
  onPageChange,
  onSearch,
  bgColor = "bg-white",
  borderNone = true,
  tableHeaderBg="muted/80",
  isLoading = false,
  showPagination = true,
}) => {
  /**
   * Handle page navigation
   * @param {number} page - Target page number
   */
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && onPageChange) {
      onPageChange(page);
    }
  };

  /**
   * Generate page numbers for pagination
   */
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const start = Math.max(1, currentPage - 2);
      const end = Math.min(totalPages, start + maxVisiblePages - 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  return (
    <div className="space-y-4">
      {/* Table */}
      <div
        className={`rounded-md ${!borderNone ? "border-none" : "border"} ${bgColor}`}
      >
        <Table>
          <TableHeader>
            <TableRow
              className={cn(
                `bg-${tableHeaderBg} ${!borderNone ? "border-none" : ""}`,
              )}
            >
              {columns.map((column, index) => (
                <TableHead
                  key={index}
                  className={cn(
                    "font-semibold text-responsive-sm",
                    column.className,
                  )}
                >
                  {column.header}
                </TableHead>
              ))}
              {/* Actions column */}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // Loading skeleton
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  {columns.map((_, colIndex) => (
                    <TableCell key={colIndex}>
                      <div className="h-4 bg-muted animate-pulse rounded" />
                    </TableCell>
                  ))}
                  <TableCell>
                    <div className="h-4 w-8 bg-muted animate-pulse rounded" />
                  </TableCell>
                </TableRow>
              ))
            ) : data.length === 0 ? (
              // Empty state
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  className="text-center py-8 text-muted-foreground"
                >
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              // Data rows
              data.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  className={cn(
                    `hover:bg-muted/15 ${!borderNone ? "border-none" : ""}`,
                  )}
                >
                  {columns.map((column, colIndex) => (
                    <TableCell
                      key={colIndex}
                      className={cn(
                        "text-responsive-sm min-w-[100px]",
                        column.cellClassName,
                      )}
                    >
                      {column.render
                        ? column.render(row)
                        : row[column.accessor]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {showPagination && (
        <div className="flex  flex-row items-center justify-between gap-4">
          <div className="text-responsive-sm text-foreground">
            Page {currentPage} of {totalPages}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="neutral"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="text-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>

            <div className="flex items-center gap-1">
              {getPageNumbers().map((page) => (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "neutral"}
                  size="sm"
                  onClick={() => handlePageChange(page)}
                  className="w-10"
                >
                  {page}
                </Button>
              ))}
            </div>

            <Button
              variant="neutral"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
